import * as React from 'react'
import { useDrag } from '@use-gesture/react'
import { cn } from '@/lib/utils'
import { MoreVertical } from '@/components/icons'
import { useIsDesktop } from './use-mobile'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu'

export interface SwipeableRowProps {
  children: React.ReactNode
  onDelete: () => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  disabled?: boolean
  actionWidth?: number
  className?: string
  /**
   * Background of the swiping foreground — it hides the action buttons while
   * the row is closed, so it must match the surface behind the row.
   */
  foregroundClassName?: string
  actionLabel?: string
  actionClassName?: string
  pending?: boolean
  pendingLabel?: string
  onEdit?: () => void
  editLabel?: string
  editClassName?: string
  /** 'bar' (default) = full-height rectangular text buttons. 'icon' = rounded chip with a white icon (swipe indicator). */
  variant?: 'bar' | 'icon'
  /** Show the label inside the 'icon' variant chip (below the icon). Default false = icon-only chip. */
  showIconLabels?: boolean
  /** Icon for the primary (onDelete) action — used by the 'icon' variant. */
  actionIcon?: React.ReactNode
  /** Icon for the secondary (onEdit) action — used by the 'icon' variant. */
  editIcon?: React.ReactNode
}

const MAX_OVERSHOOT = 1.25

const SwipeableRow = React.forwardRef<HTMLDivElement, SwipeableRowProps>(
  (
    {
      children,
      onDelete,
      isOpen,
      onOpenChange,
      disabled = false,
      actionWidth,
      className,
      foregroundClassName,
      actionLabel = 'Remove',
      actionClassName,
      pending = false,
      pendingLabel = 'Removing...',
      onEdit,
      editLabel = 'Edit',
      editClassName,
      variant = 'bar',
      showIconLabels = false,
      actionIcon,
      editIcon,
    },
    ref
  ) => {
    const isIcon = variant === 'icon'
    const chipClass =
      'flex flex-col items-center justify-center gap-0.5 rounded-lg px-3 py-1.5 text-white [&>svg]:size-4'
    const resolvedActionWidth = actionWidth ?? (isIcon ? 56 : 72)
    const totalWidth = onEdit ? resolvedActionWidth * 2 : resolvedActionWidth
    const [offset, setOffset] = React.useState(isOpen ? totalWidth : 0)
    const [dragging, setDragging] = React.useState(false)
    const movedRef = React.useRef(false)

    React.useEffect(() => {
      if (!dragging) {
        setOffset(isOpen ? totalWidth : 0)
      }
    }, [isOpen, totalWidth, dragging])

    const clamp = (v: number) => {
      const max = totalWidth * MAX_OVERSHOOT
      if (v < 0) return 0
      if (v > max) return max
      return v
    }

    const bind = useDrag(
      ({ down, movement: [mx], direction: [dx], cancel, first, tap }) => {
        if (tap) return

        if (first) {
          movedRef.current = false
        }

        movedRef.current = true
        setDragging(down)

        const startOffset = isOpen ? totalWidth : 0
        const next = clamp(startOffset - mx)
        setOffset(next)

        if (!down) {
          const shouldOpen = next > totalWidth / 2
          setOffset(shouldOpen ? totalWidth : 0)
          if (shouldOpen !== isOpen) onOpenChange(shouldOpen)
          movedRef.current = false
        }
      },
      {
        axis: 'x',
        filterTaps: true,
        enabled: !disabled,
        pointer: { touch: true },
      }
    )

    const isDesktop = useIsDesktop()

    const handleForegroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (movedRef.current) {
        e.preventDefault()
        e.stopPropagation()
        movedRef.current = false
        return
      }
      if (isOpen) {
        e.preventDefault()
        e.stopPropagation()
        onOpenChange(false)
      }
    }

    // Desktop has no swipe gesture — surface the same actions in a kebab
    // dropdown menu instead. No actionClassName means the default red swipe
    // chip, i.e. a true destructive action.
    if (isDesktop) {
      const isDestructive = !actionClassName
      return (
        <div ref={ref} className={cn('flex items-center gap-1', className)}>
          <div className="min-w-0 flex-1">{children}</div>
          {!disabled && (
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label="Row actions"
                onClick={(e) => e.stopPropagation()}
                className="flex size-8 shrink-0 items-center justify-center rounded-full outline-none hover:bg-surface-L2"
              >
                <MoreVertical className="size-4 text-content-primary-subtle" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    className="text-content-primary-bold focus:text-content-primary-bold"
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit()
                    }}
                  >
                    {editIcon}
                    {editLabel}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  variant={isDestructive ? 'destructive' : undefined}
                  className={
                    isDestructive
                      ? undefined
                      : 'text-content-primary-bold focus:text-content-primary-bold'
                  }
                  disabled={pending}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                >
                  {actionIcon}
                  {pending ? pendingLabel : actionLabel}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('relative overflow-hidden', className)}
      >
        <div
          className="absolute inset-y-0 right-0 flex items-stretch"
          style={{ width: totalWidth }}
          aria-hidden={!isOpen}
        >
          {onEdit && (
            <button
              type="button"
              disabled={pending}
              onClick={(e) => {
                e.stopPropagation()
                if (pending) return
                onEdit()
              }}
              tabIndex={isOpen ? 0 : -1}
              style={{ width: resolvedActionWidth }}
              className={cn(
                'flex items-center justify-center disabled:opacity-70',
                !isIcon &&
                  cn('text-white text-xs font-medium', editClassName ?? 'bg-content-info-bold')
              )}
              aria-label={editLabel}
            >
              {isIcon ? (
                <span className={cn(chipClass, editClassName ?? 'bg-content-info-bold')}>
                  {editIcon}
                  {showIconLabels && (
                    <span className="text-xs font-semibold leading-4">{editLabel}</span>
                  )}
                </span>
              ) : (
                editLabel
              )}
            </button>
          )}
          <button
            type="button"
            disabled={pending}
            onClick={(e) => {
              e.stopPropagation()
              if (pending) return
              onDelete()
            }}
            tabIndex={isOpen ? 0 : -1}
            style={{ width: resolvedActionWidth }}
            className={cn(
              'flex items-center justify-center disabled:opacity-70',
              !isIcon &&
                cn(
                  'text-white text-xs font-medium rounded-r-lg',
                  actionClassName ?? 'bg-background-negative-default'
                )
            )}
            aria-label={pending ? pendingLabel : actionLabel}
          >
            {isIcon ? (
              <span className={cn(chipClass, actionClassName ?? 'bg-background-negative-default')}>
                {actionIcon}
                {showIconLabels && (
                  <span className="text-xs font-semibold leading-4">
                    {pending ? pendingLabel : actionLabel}
                  </span>
                )}
              </span>
            ) : pending ? (
              pendingLabel
            ) : (
              actionLabel
            )}
          </button>
        </div>

        <div
          {...bind()}
          className={cn('relative', foregroundClassName ?? 'bg-surface-L1')}
          style={{
            transform: `translate3d(${-offset}px, 0, 0)`,
            transition: dragging ? 'none' : 'transform 200ms ease-out',
            touchAction: 'pan-y',
          }}
          onClickCapture={handleForegroundClick}
        >
          {children}
        </div>
      </div>
    )
  }
)

SwipeableRow.displayName = 'SwipeableRow'

export { SwipeableRow }
